/**
 * ActiveMelsPage
 * Page for viewing and managing all active MELs
 */

import React, { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useActiveAircraft } from '@/hooks/useAircraft';
import {
  useActiveMels,
  useActiveMelsByAircraft,
  useExpiringMels,
  useClearMel,
} from '@/hooks/useMel';
import {
  ActiveMelCard,
  MelExpiryWarning,
} from '@/components/mel';
import Button from '@/components/ui/Button';
import type { ActiveMel } from '@/types/mel';
import melService from '@/services/melService';
import {
  WrenchScrewdriverIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

export const ActiveMelsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  // Filter state
  const [filterAircraft, setFilterAircraft] = useState<string>(
    searchParams.get('aircraft') || ''
  );
  const [filterStatus, setFilterStatus] = useState<'all' | 'expiring' | 'expired'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Clear MEL dialog state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [melToClear, setMelToClear] = useState<ActiveMel | null>(null);
  const [clearNotes, setClearNotes] = useState('');

  // Fetch data
  const { data: allActiveMels = [], isLoading: isLoadingAll } = useActiveMels();
  const { data: expiringMels = [] } = useExpiringMels(3);
  const { data: aircraft = [] } = useActiveAircraft();

  // Fetch MELs for specific aircraft if filtered
  const { data: aircraftMels = [] } = useActiveMelsByAircraft(
    filterAircraft ? Number(filterAircraft) : 0,
    !!filterAircraft
  );

  // Clear MEL mutation
  const clearMelMutation = useClearMel();

  // Determine which MELs to show
  let displayMels = filterAircraft ? aircraftMels : allActiveMels;

  // Apply status filter
  if (filterStatus === 'expiring') {
    displayMels = displayMels.filter(
      (m) => melService.isExpiringSoon(m.expiresAt) && !melService.isExpired(m.expiresAt)
    );
  } else if (filterStatus === 'expired') {
    displayMels = displayMels.filter((m) => melService.isExpired(m.expiresAt));
  }

  // Apply search filter
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    displayMels = displayMels.filter(
      (m) =>
        m.melItem.itemNumber.toLowerCase().includes(query) ||
        m.melItem.title.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query) ||
        m.aircraft.tailNumber.toLowerCase().includes(query)
    );
  }

  // Sort by expiry (expired first, then by days remaining)
  displayMels = [...displayMels].sort((a, b) => {
    const daysA = melService.getDaysUntilExpiry(a.expiresAt);
    const daysB = melService.getDaysUntilExpiry(b.expiresAt);

    // Items with no expiry go last
    if (daysA === null && daysB === null) return 0;
    if (daysA === null) return 1;
    if (daysB === null) return -1;

    // Expired items first, then by ascending days
    return daysA - daysB;
  });

  // Handle filter changes
  const handleAircraftFilter = (value: string) => {
    setFilterAircraft(value);
    if (value) {
      setSearchParams({ aircraft: value });
    } else {
      setSearchParams({});
    }
  };

  // Handle clear MEL
  const handleClearClick = (mel: ActiveMel) => {
    setMelToClear(mel);
    setClearNotes('');
    setClearDialogOpen(true);
  };

  const handleClearConfirm = useCallback(async () => {
    if (!melToClear || !clearNotes.trim()) return;

    try {
      await clearMelMutation.mutateAsync({
        melId: melToClear.id,
        request: { notes: clearNotes.trim() },
      });
      showSuccess('MEL cleared successfully');
      setClearDialogOpen(false);
      setMelToClear(null);
      setClearNotes('');
    } catch (error) {
      showError(
        error instanceof Error ? error.message : 'Failed to clear MEL'
      );
    }
  }, [melToClear, clearNotes, clearMelMutation, showSuccess, showError]);

  // Stats
  const expiredCount = allActiveMels.filter((m) => melService.isExpired(m.expiresAt)).length;
  const expiringSoonCount = allActiveMels.filter(
    (m) => melService.isExpiringSoon(m.expiresAt) && !melService.isExpired(m.expiresAt)
  ).length;

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <WrenchScrewdriverIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Active MELs</h1>
              <p className="text-foreground/60">
                {allActiveMels.length} active MEL{allActiveMels.length !== 1 ? 's' : ''} across fleet
              </p>
            </div>
          </div>

          <Link to="/mel/search">
            <Button variant="primary" icon={<PlusIcon className="w-4 h-4" />}>
              Issue New MEL
            </Button>
          </Link>
        </div>

        {/* Expiry Warning Banner */}
        {expiringMels.length > 0 && (
          <div className="mb-6">
            <MelExpiryWarning mels={expiringMels} showLink={false} />
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div
            className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setFilterStatus('all')}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <WrenchScrewdriverIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{allActiveMels.length}</p>
                <p className="text-sm text-foreground/60">Total Active</p>
              </div>
            </div>
          </div>

          <div
            className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setFilterStatus('expiring')}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{expiringSoonCount}</p>
                <p className="text-sm text-foreground/60">Expiring Soon</p>
              </div>
            </div>
          </div>

          <div
            className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setFilterStatus('expired')}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <XMarkIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{expiredCount}</p>
                <p className="text-sm text-foreground/60">Expired</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by item number, title, or aircraft..."
                className="w-full h-10 pl-10 pr-4 rounded-md border border-border bg-background text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>

            {/* Aircraft Filter */}
            <div className="sm:w-48">
              <div className="relative">
                <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
                <select
                  value={filterAircraft}
                  onChange={(e) => handleAircraftFilter(e.target.value)}
                  className="w-full h-10 pl-9 pr-4 rounded-md border border-border bg-background text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                >
                  <option value="">All Aircraft</option>
                  {aircraft.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.tailNumber}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-40">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'expiring' | 'expired')}
                className="w-full h-10 px-4 rounded-md border border-border bg-background text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                <option value="all">All Status</option>
                <option value="expiring">Expiring Soon</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>

        {/* MEL List */}
        {isLoadingAll ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : displayMels.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <WrenchScrewdriverIcon className="w-12 h-12 mx-auto text-foreground/30 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {searchQuery || filterAircraft || filterStatus !== 'all'
                ? 'No MELs Match Your Filters'
                : 'No Active MELs'}
            </h3>
            <p className="text-foreground/60 mb-4">
              {searchQuery || filterAircraft || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'All aircraft are currently operating with no MEL items.'}
            </p>
            {(searchQuery || filterAircraft || filterStatus !== 'all') && (
              <Button
                variant="secondary"
                onClick={() => {
                  setSearchQuery('');
                  setFilterAircraft('');
                  setFilterStatus('all');
                  setSearchParams({});
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {displayMels.map((mel) => (
              <ActiveMelCard
                key={mel.id}
                mel={mel}
                onClear={handleClearClick}
                showAircraft={!filterAircraft}
              />
            ))}
          </div>
        )}
      </div>

      {/* Clear MEL Dialog */}
      {clearDialogOpen && melToClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setClearDialogOpen(false)}
          />

          <div className="relative w-full max-w-md bg-background rounded-xl shadow-2xl border border-border">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">Clear MEL</h2>
              <button
                onClick={() => setClearDialogOpen(false)}
                className="p-1 rounded-md hover:bg-muted transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-foreground/60" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="bg-muted rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm font-bold text-primary">
                    {melToClear.melItem.itemNumber}
                  </span>
                  <span className="text-xs text-foreground/50">
                    {melToClear.aircraft.tailNumber}
                  </span>
                </div>
                <p className="text-sm text-foreground">{melToClear.melItem.title}</p>
              </div>

              <div>
                <label
                  htmlFor="clear-notes"
                  className="block text-sm font-medium text-foreground mb-1.5"
                >
                  Clearance Notes <span className="text-danger">*</span>
                </label>
                <textarea
                  id="clear-notes"
                  value={clearNotes}
                  onChange={(e) => setClearNotes(e.target.value)}
                  placeholder="Describe the repair/correction that was made..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
              <Button variant="secondary" onClick={() => setClearDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleClearConfirm}
                disabled={!clearNotes.trim() || clearMelMutation.isPending}
                loading={clearMelMutation.isPending}
                icon={<CheckCircleIcon className="w-4 h-4" />}
              >
                Clear MEL
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveMelsPage;
